import React from 'react';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  Activity 
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { mockAdminMetrics } from '../data/mockData';

const Admin = ({ darkMode, toggleDarkMode }) => {
  const metrics = mockAdminMetrics;

  const COLORS = ['#0ea5e9', '#10b981', '#f59e0b'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="flex">
        <Sidebar />

        <main className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              System metrics and analytics
            </p>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-green-600 text-sm font-medium">+12%</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {metrics.totalUsers.toLocaleString()}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-green-600 text-sm font-medium">+8%</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Active Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {metrics.activeUsers.toLocaleString()}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-green-600 text-sm font-medium">+26%</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Total Papers</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {metrics.totalPapers.toLocaleString()}
              </p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-yellow-600" />
                </div>
                <span className="text-green-600 text-sm font-medium">+2.3%</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Success Rate</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {metrics.successRate}%
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Growth Chart */}
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Monthly Growth
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics.monthlyGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="month" 
                    stroke={darkMode ? '#9ca3af' : '#6b7280'}
                  />
                  <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#0ea5e9" 
                    strokeWidth={2}
                    name="Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="papers" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Papers"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Plan Distribution */}
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Plan Distribution
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={metrics.planDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {metrics.planDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-4 mt-6">
                {metrics.planDistribution.map((plan, index) => (
                  <div key={index} className="text-center">
                    <div 
                      className="w-4 h-4 rounded-full mx-auto mb-2"
                      style={{ backgroundColor: COLORS[index] }}
                    ></div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {plan.name}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {plan.value}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card mt-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              System Health
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200 mb-1">
                  API Status
                </p>
                <p className="text-2xl font-bold text-green-600">Operational</p>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-1">
                  Avg Response Time
                </p>
                <p className="text-2xl font-bold text-blue-600">234ms</p>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
                <p className="text-sm text-purple-800 dark:text-purple-200 mb-1">
                  Uptime (30 days)
                </p>
                <p className="text-2xl font-bold text-purple-600">99.97%</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Admin;